/**
 * Screen flow: auth → menu → lobby (or quick-play matchmaking) → match → results. One
 * websocket for everything online; offline matches run in a worker behind the same MatchLink.
 * The link's health is always on screen: a dropped socket says `LINK LOST · RECONNECTING`
 * (in a match the session dims the world and freezes its clock), and once the server is gone
 * for good `CONNECTION LOST` offers RETRY / MENU. A server that is down at the start leaves
 * the offline game as the one primary button and is polled until it is back.
 */
import { botCallsign, type EndMessage, type RoomStateMessage, type ServerMessage } from '@sa/protocol';
import { GAME_NAME, occupiedSlots, regionSetFor, teamLayoutFor, type BotDifficulty, type MatchSettings, type PlayerSlot, type RegionId } from '@sa/sim';
import { loadWorldAssets, rawCopy, type WorldAssets } from '../assets';
import { LOBBY_CUE, Music, STAGE } from '../audio/music';
import { Sfx } from '../audio/sfx';
import { params } from '../config';
import { roomErrorText } from '../game/errors';
import type { MatchLink } from '../game/link';
import { GameSession, type SessionResult } from '../game/session';
import { api, ApiError, MIN_PASSWORD, SERVER_DOWN, type Me } from '../net/api';
import { GameSocket, type SocketState } from '../net/socket';
import { track } from '../net/track';
import { OfflineLink } from '../offline/offlineLink';
import { clock } from '../state/alerts';
import { h, setText } from './dom';
import { badCharText, cleanCode, formProblem, isFullCode } from './forms';
import { LobbyScreen, type LobbyModel } from './screens/lobby';
import { resultsScreen, setResultActions, type ResultAction } from './screens/results';
import { sampleChronicle } from './screens/resultsSample';
import { ChatPanel } from './chat';
import { drawWorld } from './worldmap';
import { touchOnly } from './device';

/** GAME_NAME with its word break back (`ScaledAttack` → `SCALED ATTACK`) once upper-cased. */
export const LOGO = GAME_NAME.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
/** while the server is unreachable from the menu / auth screen, ask again this often */
const SLOW_RETRY_MS = 10_000;
/** after a reconnect in a lobby, the room must answer within this long (else it is gone) */
const ROOM_RECHECK_MS = 4000;
/** after a reconnect in a match, the seat must come back (welcome) within this long */
const SEAT_RECHECK_MS = 8000;

function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] >>> 1;
}

export class App {
  private assets: WorldAssets | null = null;
  private me: Me | null = null;
  private sock: GameSocket | null = null;
  private sfx = new Sfx();
  private music = new Music(this.sfx);
  /** chat (online): the room lobby's and the match's — one log per room */
  private chat = new ChatPanel((m) => this.sock?.send(m) ?? false, this.sfx);
  private chatRoom: string | null = null;
  private screen: HTMLElement | null = null;
  private lobby: LobbyScreen | null = null;
  private session: GameSession | null = null;
  private bg = h('canvas.screen-bg');
  private err = h('div.err');
  private offlineSetup: { slots: PlayerSlot[]; settings: MatchSettings } | null = null;
  private room: RoomStateMessage | null = null;
  /** the room the running match came from (play again: quick play or back to the room) */
  private matchRoom: { quickplay: boolean; code: string } | null = null;
  /** offline: the same setup again from the results */
  private offlineRematch: (() => void) | null = null;
  /** we left a match while the link was down: say so to the server once it is back (and do not walk back in) */
  private leaveOnReconnect = false;
  private ignoreMatchUntil = 0;
  /** the last lobby request (its `room.error` is worded for it) */
  private lastRequest: string | null = null;
  /** link trouble outside a match: one line at the top of the screen */
  private linkBar = h('div.link-bar');
  private linkText = h('span');
  private linkBtn = h('button.act', {}, 'RETRY');
  private linkTimer: ReturnType<typeof setInterval> | null = null;
  private slowRetry: ReturnType<typeof setInterval> | null = null;
  private recheck: ReturnType<typeof setTimeout> | null = null;
  /** the server did not answer at start: the auth screen polls until it does */
  private healthPoll: ReturnType<typeof setInterval> | null = null;

  constructor(private root: HTMLElement) {
    window.addEventListener('resize', () => this.drawBg());
    root.addEventListener('pointerdown', () => this.sfx.unlock());
    this.linkBar.append(this.linkText, this.linkBtn);
    this.linkBar.hidden = true;
    root.append(this.linkBar);
  }

  async start(): Promise<void> {
    const phone = touchOnly();
    track('page_view', {
      ref: document.referrer ? new URL(document.referrer).hostname : '',
      w: innerWidth,
      h: innerHeight,
      dpr: devicePixelRatio,
      phone,
      bot: navigator.webdriver,
      lang: navigator.language,
    });
    // phones and touch-only tablets: no game without a mouse and keys (`ui/device.ts`)
    if (phone && !params.offline && !params.screen) {
      this.desktopNeeded();
      return;
    }
    this.show(this.loadingScreen('LOADING'));
    try {
      this.assets = await loadWorldAssets();
    } catch {
      this.show(this.messageScreen('MAP DATA UNAVAILABLE', 'RELOAD THE PAGE TO TRY AGAIN'));
      return;
    }
    if (params.offline) {
      this.offlineSetup = this.defaultOffline(params.bots, params.difficulty, params.region);
      this.startOffline(params.seed || randomSeed(), params.speed, Math.round(params.skipSeconds * 10));
      return;
    }
    if (params.screen) {
      this.devScreen(params.screen);
      return;
    }
    await this.boot();
  }

  /** Who are we? Signed in → menu; signed out → auth; no server → auth with offline play first. */
  private async boot(): Promise<void> {
    try {
      this.me = await api.me();
    } catch {
      this.me = null;
      this.showAuth(true);
      return;
    }
    this.stopHealthPoll();
    if (!this.me) this.showAuth();
    else await this.afterLogin();
  }

  // ------------------------------------------------------------------ plumbing

  private show(el: HTMLElement): void {
    this.chat.unmount();
    this.lobby?.dispose();
    this.lobby = null;
    this.screen?.remove();
    this.screen = el;
    if (!el.classList.contains('results') && !el.classList.contains('nobg')) el.prepend(this.bg);
    this.root.append(el);
    // the menus play the lobby; the results, the aftermath (the match plays its own stages)
    this.music.set(el.classList.contains('results') ? { ...LOBBY_CUE, stage: STAGE.aftermath } : LOBBY_CUE);
    this.drawBg();
    this.renderLink();
  }

  private drawBg(): void {
    if (this.assets && this.bg.isConnected) drawWorld(this.bg, this.assets.world, { alpha: 0.13, dotted: true });
  }

  private loadingScreen(text: string, onCancel?: () => void): HTMLElement {
    return h(
      'div.screen',
      {},
      h(
        'div.col',
        {},
        h('div.session-loading', { style: { position: 'static', background: 'none' } }, h('div.t', {}, text)),
        onCancel ? h('button.btn.ghost', { onclick: onCancel, style: { justifyContent: 'center' } }, 'CANCEL') : null,
      ),
    );
  }

  /** A phone: what the game is, and that it needs a computer (with the link to take there). */
  private desktopNeeded(): void {
    const url = `${location.origin}/`;
    const share = h('button.btn.primary', {}, 'COPY THE LINK', h('span.hint', {}, location.host.toUpperCase()));
    share.onclick = () => {
      track('mobile_share');
      const done = () => (share.firstChild!.textContent = 'LINK COPIED');
      if (navigator.share) void navigator.share({ title: LOGO, url }).catch(() => {});
      else void navigator.clipboard?.writeText(url).then(done, () => {});
    };
    track('mobile_block');
    this.show(
      h(
        'div.screen.device',
        {},
        h(
          'div.col',
          {},
          h('div.logo', {}, LOGO),
          h('div.sub', {}, 'GLOBAL NUCLEAR STRATEGY'),
          h('div.err', {}, 'DESKTOP NEEDED'),
          h('div.msg', {}, 'SCALED ATTACK IS PLAYED WITH A MOUSE AND KEYBOARD · OPEN IT ON A COMPUTER'),
          share,
        ),
      ),
    );
  }

  private messageScreen(title: string, msg: string): HTMLElement {
    return h('div.screen', {}, h('div.col', {}, h('div.logo', {}, LOGO), h('div.err', { style: { textAlign: 'center' } }, title), h('div.sub', {}, msg)));
  }

  /** Callsign (click to change it), `· GUEST`, and `· OFFLINE` while the server is away. */
  private who(): HTMLElement | null {
    const me = this.me;
    if (!me) return null;
    const name = h('button.who-name', { title: 'CHANGE CALLSIGN' }, me.name.toUpperCase());
    const el = h('div.who', {}, name, me.isGuest ? h('span', {}, '\u00a0· GUEST') : null, h('span.who-off'));
    name.onclick = () => {
      const input = h('input.field.who-edit', { value: me.name, maxLength: 16, spellcheck: false });
      // Enter commits, then the swap-out blurs the input and would commit again (and
      // replaceWith a detached node throws): the first commit wins
      let committed = false;
      const done = (save: boolean) => {
        if (committed) return;
        committed = true;
        const v = input.value.trim();
        input.replaceWith(name);
        if (!save || !v || v === me.name) return;
        void this.withErr(async () => {
          const r = await api.rename(v);
          me.name = r.name;
          setText(name, r.name.toUpperCase());
        });
      };
      input.onkeydown = (e) => {
        if (e.key === 'Enter') done(true);
        else if (e.key === 'Escape') done(false);
      };
      input.onblur = () => done(true);
      name.replaceWith(input);
      input.focus();
      input.select();
    };
    return el;
  }

  private async withErr(fn: () => Promise<void>): Promise<void> {
    setText(this.err, '');
    try {
      await fn();
    } catch (e) {
      setText(this.err, e instanceof ApiError ? e.message : SERVER_DOWN);
    }
  }

  // ------------------------------------------------------------------ auth + menu

  /** Sign in / sign up / guest. `down`: the server did not answer — offline play leads and the server is polled. */
  private showAuth(down = false): void {
    let signUp = false;
    const name = h('input.field', { placeholder: 'CALLSIGN', maxLength: 16, hidden: true });
    const email = h('input.field', { placeholder: 'EMAIL', type: 'email', autocomplete: 'email' });
    const pass = h('input.field', { placeholder: 'PASSWORD', type: 'password', autocomplete: 'current-password' });
    const submit = h('button.btn', {}, 'SIGN IN', h('span.hint', {}, '↵'));
    const toggle = h('button.btn.ghost', {}, 'NO ACCOUNT? CREATE ONE');
    toggle.onclick = () => {
      signUp = !signUp;
      name.hidden = !signUp;
      pass.placeholder = signUp ? `PASSWORD · ${MIN_PASSWORD}+ CHARACTERS` : 'PASSWORD';
      submit.firstChild!.textContent = signUp ? 'CREATE ACCOUNT' : 'SIGN IN';
      toggle.textContent = signUp ? 'HAVE AN ACCOUNT? SIGN IN' : 'NO ACCOUNT? CREATE ONE';
      setText(this.err, '');
    };
    const go = () =>
      this.withErr(async () => {
        const problem = formProblem(signUp, name.value.trim(), email.value.trim(), pass.value);
        if (problem) {
          setText(this.err, problem);
          return;
        }
        if (signUp) await api.signUp(name.value.trim(), email.value.trim(), pass.value);
        else await api.signIn(email.value.trim(), pass.value);
        track(signUp ? 'signup_ok' : 'signin_ok');
        this.me = await api.me();
        await this.afterLogin();
      });
    submit.onclick = () => void go();
    // (a handler property that returns false cancels the key: never return the comparison)
    pass.onkeydown = (e) => {
      if (e.key === 'Enter') void go();
    };
    const guest = h(
      'button.btn.primary',
      {
        onclick: () =>
          void this.withErr(async () => {
            track('guest_click');
            await api.guest();
            track('guest_ok');
            this.me = await api.me();
            await this.afterLogin();
          }),
      },
      'PLAY AS GUEST',
      h('span.hint', {}, 'ONE CLICK'),
    );
    const offline = h('button.btn.ghost', { onclick: () => this.showOfflineLobby() }, 'PLAY OFFLINE VS BOTS');
    track('auth_view', { down });
    const online = [guest, name, email, pass, submit, toggle];
    if (down) {
      // the online block waits for the server; offline play is the way in meanwhile
      for (const el of online) (el as HTMLButtonElement | HTMLInputElement).disabled = true;
      guest.classList.remove('primary');
      offline.className = 'btn primary';
      setText(this.err, 'SERVER UNREACHABLE · RETRYING');
      this.startHealthPoll();
    } else setText(this.err, '');
    this.show(
      h(
        'div.screen.anchored',
        {},
        h(
          'div.col',
          {},
          h('div.logo', {}, LOGO),
          h('div.sub', {}, 'GLOBAL NUCLEAR STRATEGY'),
          ...(down ? [offline, this.err, h('div.sep', {}, 'ONLINE')] : []),
          guest,
          h('div.sep', {}, 'OR'),
          name,
          email,
          pass,
          submit,
          toggle,
          ...(down ? [] : [this.err, offline]),
        ),
      ),
    );
  }

  private startHealthPoll(): void {
    if (this.healthPoll) return;
    this.healthPoll = setInterval(() => {
      void api.healthy().then((up) => {
        if (up && this.healthPoll && !this.session) void this.boot();
      });
    }, SLOW_RETRY_MS);
  }

  private stopHealthPoll(): void {
    if (this.healthPoll) clearInterval(this.healthPoll);
    this.healthPoll = null;
  }

  private async afterLogin(): Promise<void> {
    if (!this.me) return this.showAuth();
    this.stopHealthPoll();
    try {
      await this.ensureSocket();
    } catch (e) {
      setText(this.err, e instanceof ApiError ? e.message : SERVER_DOWN);
    }
    if (params.room && this.sock?.state === 'open') {
      this.request({ type: 'room.join', code: params.room });
      history.replaceState(null, '', location.pathname);
      return;
    }
    if (!this.session && !this.room) this.showMenu();
  }

  private showMenu(): void {
    track('menu_view');
    this.room = null;
    this.clearRecheck();
    const code = h('input.field', { placeholder: 'ROOM CODE', maxLength: 7, spellcheck: false, autocomplete: 'off' });
    const joinBtn = h('button.btn.online', { style: { flex: '0 0 auto' }, disabled: true }, 'JOIN');
    const join = () => {
      const c = cleanCode(code.value).code;
      if (!isFullCode(c)) return;
      track('room_join');
      this.request({ type: 'room.join', code: c });
    };
    code.oninput = () => {
      const { code: c, bad } = cleanCode(code.value);
      if (code.value !== c) code.value = c;
      joinBtn.disabled = !isFullCode(c) || this.sock?.state !== 'open';
      setText(this.err, bad ? badCharText(bad) : '');
    };
    code.onkeydown = (e) => {
      if (e.key === 'Enter') join();
    };
    joinBtn.onclick = join;
    const createRoom = (m: Parameters<GameSocket['send']>[0]) => {
      track('room_create', { mode: m.type === 'room.create' ? m.mode : '' });
      this.request(m);
    };
    const create = h(
      'div.row-inline',
      { hidden: true },
      h('button.btn.online', { onclick: () => createRoom({ type: 'room.create', mode: 'ffa', maxPlayers: 8, private: true }) }, 'FFA'),
      h('button.btn.online', { onclick: () => createRoom({ type: 'room.create', mode: 'teams', maxPlayers: 8, teamCount: 2, private: true }) }, '2 TEAMS'),
    );
    const sound = (muted: boolean) => `SOUND · ${muted ? 'OFF' : 'ON'}`;
    const mute = h('button.btn.ghost', {}, sound(this.sfx.muted));
    mute.onclick = () => (mute.textContent = sound(this.sfx.toggleMute()));
    const music = h('button.btn.ghost', {}, this.music.label);
    music.onclick = () => (music.textContent = this.music.cycle());
    this.show(
      h(
        'div.screen.anchored',
        {},
        this.who(),
        h(
          'div.col',
          {},
          h('div.logo', {}, LOGO),
          h('div.sub', {}, '20 MIN · 2–8 NATIONS · ONE PLANET'),
          h('button.btn.primary.online', { onclick: () => this.quickPlay() }, 'QUICK PLAY', h('span.hint', {}, 'MATCHMAKING')),
          h('button.btn.online', { onclick: () => (create.hidden = !create.hidden) }, 'CREATE ROOM'),
          create,
          h('div.row-inline', {}, code, joinBtn),
          h('button.btn', { onclick: () => this.showOfflineLobby() }, 'PLAY OFFLINE VS BOTS'),
          this.err,
          h(
            'div.row-inline',
            {},
            mute,
            music,
            h(
              'button.btn.ghost',
              {
                onclick: () =>
                  void this.withErr(async () => {
                    await api.signOut();
                    this.sock?.close();
                    this.sock = null;
                    this.me = null;
                    this.renderLink();
                    this.showAuth();
                  }),
              },
              'SIGN OUT',
            ),
          ),
        ),
      ),
    );
    this.renderLink();
  }

  // ------------------------------------------------------------------ online

  private async ensureSocket(): Promise<GameSocket> {
    if (this.sock) {
      if (this.sock.state === 'closed') this.sock.retry();
      return this.sock;
    }
    const s = new GameSocket();
    this.sock = s;
    s.on((m) => this.onServer(m));
    s.onState((st) => this.onLink(st));
    await s.connect();
    return s;
  }

  /** Send a lobby request; with the link down, say so instead. */
  private request(m: Parameters<GameSocket['send']>[0]): void {
    setText(this.err, '');
    this.lastRequest = m.type;
    if (!this.sock?.send(m)) {
      setText(this.err, SERVER_DOWN);
      this.sock?.retry();
    }
  }

  private quickPlay(): void {
    track('quickplay_click');
    this.request({ type: 'quickplay.join' });
    if (this.sock?.state !== 'open') return;
    this.show(
      this.loadingScreen('FINDING A MATCH', () => {
        track('quickplay_cancel');
        this.sock?.send({ type: 'quickplay.leave' });
        this.showMenu();
      }),
    );
  }

  private onServer(m: ServerMessage): void {
    switch (m.type) {
      case 'room.state':
        this.clearRecheck();
        this.room = m;
        if (!this.session) this.showRoom(m);
        else this.refreshResults();
        break;
      case 'room.error': {
        if (m.code === 'resumeExpired') {
          this.session?.linkDead('MATCH NO LONGER AVAILABLE', 'IT ENDED OR YOUR SEAT WAS RELEASED');
          break;
        }
        if (m.code === 'notInRoom') {
          // a leave that crossed the room's end (harmless), or a lobby that is gone
          if (this.room && !this.session) {
            this.showMenu();
            setText(this.err, 'ROOM CLOSED');
          }
          break;
        }
        const text = roomErrorText(m.code, this.lastRequest);
        setText(this.err, text);
        // a join that failed (or a lobby that vanished) goes back to the menu, with the reason
        if ((m.code === 'notFound' || m.code === 'inMatch' || m.code === 'full') && !this.session && !this.room) {
          this.showMenu();
          setText(this.err, text);
        }
        break;
      }
      case 'room.left':
        if (m.reason === 'left' || this.session) {
          if (this.session) this.room = null;
          this.refreshResults();
          break;
        }
        this.showMenu();
        setText(this.err, m.reason === 'kicked' ? 'REMOVED FROM ROOM' : 'ROOM CLOSED');
        break;
      case 'match.starting':
        if (performance.now() >= this.ignoreMatchUntil) this.startOnline(m.countdownMs);
        break;
      case 'welcome':
        if (!this.session && performance.now() >= this.ignoreMatchUntil) this.startOnline(0);
        break;
      case 'chat':
      case 'chat.error':
        this.chat.receive(m);
        break;
      default:
        break;
    }
  }

  /** The socket's state changed: tell the match, or the screen around it. */
  private onLink(st: SocketState): void {
    const sock = this.sock;
    if (!sock) return;
    const s = this.session && !this.session.isOffline ? this.session : null;
    if (st === 'open') {
      this.stopSlowRetry();
      if (this.leaveOnReconnect) {
        // the server re-seats us in the match we left while offline: leave it for real
        this.leaveOnReconnect = false;
        this.ignoreMatchUntil = performance.now() + 5000;
        sock.send({ type: 'room.leave' });
      }
      if (s) {
        // the seat must come back (welcome) — else the match is gone
        this.clearRecheck();
        this.recheck = setTimeout(() => s.linkDead('MATCH NO LONGER AVAILABLE', 'IT ENDED OR YOUR SEAT WAS RELEASED'), SEAT_RECHECK_MS);
      } else if (this.room) {
        // the server re-attaches a lobby member with room.state; silence means the room is gone
        this.clearRecheck();
        this.recheck = setTimeout(() => {
          this.showMenu();
          setText(this.err, 'ROOM CLOSED');
        }, ROOM_RECHECK_MS);
      }
    } else if (st === 'reconnecting') {
      s?.linkLost(sock.lostAt, () => sock.retry());
    } else if (st === 'closed') {
      if (s) s.linkClosed(sock.closeReason ?? 'lost', () => sock.retry());
      else if (sock.closeReason === 'lost' && this.me) this.startSlowRetry();
    }
    this.renderLink();
  }

  /** Off the match: the link bar, the OFFLINE tag and the online buttons follow the socket. */
  private renderLink(): void {
    const sock = this.sock;
    const st = sock?.state ?? 'open';
    const why = sock?.closeReason;
    const trouble = !!sock && !this.session && this.me !== null && (st === 'reconnecting' || (st === 'closed' && why !== 'byUs'));
    this.linkBar.hidden = !trouble;
    const online = st === 'open' || !sock;
    this.screen?.querySelectorAll<HTMLButtonElement>('.online').forEach((b) => (b.disabled = !online));
    const off = this.screen?.querySelector<HTMLElement>('.who-off');
    if (off) setText(off, online ? '' : '\u00a0· OFFLINE');
    if (!trouble) {
      if (this.linkTimer) clearInterval(this.linkTimer);
      this.linkTimer = null;
      return;
    }
    const paint = () => {
      if (sock!.state === 'reconnecting') {
        setText(this.linkText, `LINK LOST · RECONNECTING ${clock(sock!.downSeconds())}`);
        this.linkBtn.hidden = true;
        return;
      }
      this.linkBtn.hidden = false;
      if (why === 'unauthorized') {
        setText(this.linkText, 'SESSION EXPIRED');
        setText(this.linkBtn, 'SIGN IN');
        this.linkBtn.onclick = () => {
          this.sock = null;
          this.me = null;
          this.renderLink();
          this.showAuth();
        };
      } else {
        setText(this.linkText, why === 'replaced' ? 'SIGNED IN FROM ANOTHER TAB' : 'OFFLINE · SERVER UNREACHABLE');
        setText(this.linkBtn, why === 'replaced' ? 'PLAY HERE' : 'RETRY');
        this.linkBtn.onclick = () => sock!.retry();
      }
    };
    paint();
    this.linkTimer ??= setInterval(paint, 500);
  }

  private startSlowRetry(): void {
    this.slowRetry ??= setInterval(() => {
      if (!this.session && this.sock?.state === 'closed' && this.sock.closeReason === 'lost') this.sock.retry();
    }, SLOW_RETRY_MS);
  }

  private stopSlowRetry(): void {
    if (this.slowRetry) clearInterval(this.slowRetry);
    this.slowRetry = null;
  }

  private clearRecheck(): void {
    if (this.recheck) clearTimeout(this.recheck);
    this.recheck = null;
  }

  private showRoom(r: RoomStateMessage): void {
    const sock = this.sock!;
    const meSlot = r.slots.find((s) => s.userId === this.me?.id)?.slot ?? -1;
    const model: LobbyModel = {
      code: r.code,
      slots: r.slots,
      settings: r.settings,
      me: meSlot,
      isHost: r.hostId === this.me?.id,
      hostSlot: r.slots.find((s) => s.userId === r.hostId)?.slot ?? -1,
      regionSet: r.regionSet,
      teamRegions: r.teamRegions ?? null,
      quickplay: r.quickplay ? { fillAt: r.fillAt ?? null, starting: r.phase !== 'lobby' } : null,
      setSlot: (slot, kind, difficulty, team) =>
        this.request({ type: 'room.setSlot', slot, kind, ...(difficulty ? { difficulty } : {}), ...(team ? { team } : {}) }),
      pickRegion: (regionId) => this.request({ type: 'room.pickRegion', regionId }),
      ready: (ready) => this.request({ type: 'room.ready', ready }),
      start: () => this.request({ type: 'room.start' }),
      leave: () => {
        track(r.quickplay ? 'quickplay_cancel' : 'room_leave');
        sock.send(r.quickplay ? { type: 'quickplay.leave' } : { type: 'room.leave' });
        this.showMenu();
      },
    };
    if (r.code !== this.chatRoom) {
      this.chatRoom = r.code;
      this.chat.reset();
    }
    if (!this.lobby) {
      const l = new LobbyScreen(this.assets!.world, this.err);
      this.show(l.el);
      this.lobby = l;
      this.chat.mountLobby(l.el, () => this.me?.name ?? '');
    }
    this.lobby.update(model);
  }

  private startOnline(countdownMs: number): void {
    if (this.session || !this.sock) return;
    track('match_enter', { quickplay: !!this.room?.quickplay, humans: this.room?.slots.filter((x) => x.kind === 'human').length ?? 0 });
    this.matchRoom = this.room ? { quickplay: this.room.quickplay, code: this.room.code } : null;
    this.enterMatch(this.sock.matchLink(), null, countdownMs > 0 ? 'MATCH STARTS IN' : 'ESTABLISHING LINK', countdownMs);
  }

  // ------------------------------------------------------------------ offline

  private defaultOffline(bots: number, diff: BotDifficulty, region: RegionId | null): { slots: PlayerSlot[]; settings: MatchSettings } {
    const slots: PlayerSlot[] = [{ slot: 0, kind: 'human', name: this.me?.name ?? 'YOU', team: 1, regionId: region, ready: true }];
    for (let i = 1; i < 8; i++) {
      slots.push(
        i <= bots
          ? { slot: i, kind: 'bot', name: botCallsign(i), difficulty: diff, team: i + 1, regionId: null, ready: true }
          : { slot: i, kind: 'closed', name: '', team: i + 1, regionId: null, ready: false },
      );
    }
    return { slots, settings: { mode: 'ffa', maxPlayers: 8 } };
  }

  private showOfflineLobby(): void {
    track('offline_lobby');
    this.offlineSetup ??= this.defaultOffline(5, 'normal', null);
    const setup = this.offlineSetup;
    setText(this.err, '');
    const lobby = new LobbyScreen(this.assets!.world, this.err);
    const regionSet = () => this.regionSetOf(setup.slots);
    const refresh = () => {
      // like the server: the region set follows the seat count; a pick outside it is cleared
      const set = regionSet();
      const mine = setup.slots[0].regionId;
      if (mine != null && !set.includes(mine)) setup.slots[0] = { ...setup.slots[0], regionId: null };
      lobby.update(model);
    };
    const model: LobbyModel = {
      code: null,
      get slots() {
        return setup.slots;
      },
      get regionSet() {
        return regionSet();
      },
      settings: setup.settings,
      me: 0,
      isHost: true,
      hostSlot: 0,
      setSlot: (slot, kind, difficulty) => {
        setup.slots[slot] =
          kind === 'bot'
            ? { ...setup.slots[slot], kind, name: botCallsign(slot), difficulty: difficulty ?? 'normal', ready: true }
            : { ...setup.slots[slot], kind: 'closed', name: '', ready: false };
        refresh();
      },
      pickRegion: (id) => {
        setup.slots[0] = { ...setup.slots[0], regionId: id };
        refresh();
      },
      ready: () => {},
      start: () => {
        track('offline_start', { bots: occupiedSlots(setup.slots).length - 1 });
        this.startOffline(randomSeed(), 1, 0);
      },
      leave: () => (this.me ? this.showMenu() : this.boot()),
    };
    this.show(lobby.el);
    this.lobby = lobby;
    refresh();
  }

  /** The regions an offline match of these seats plays on (BALANCE.regionSets; all for 8). */
  private regionSetOf(slots: PlayerSlot[]): RegionId[] {
    const world = this.assets!.world;
    return regionSetFor(world, occupiedSlots(slots).length) ?? world.regions.map((r) => r.id);
  }

  private startOffline(seed: number, speed: number, skipTicks: number): void {
    const setup = this.offlineSetup!;
    const link = new OfflineLink({
      world: rawCopy(this.assets!),
      seed,
      settings: setup.settings,
      slots: setup.slots,
      speed,
      skipTicks,
      until: params.until,
      autopilot: params.autopilot,
      ...(params.devElim !== null ? { devElimTick: Math.round(params.devElim * 10) } : {}),
      ...(params.devCrit !== null ? { devCritTick: Math.round(params.devCrit * 10) } : {}),
      ...(params.devRivalCrit !== null ? { devRivalCritTick: Math.round(params.devRivalCrit * 10) } : {}),
      ...(params.devStrike !== null ? { devStrikeTick: Math.round(params.devStrike * 10) } : {}),
      ...(params.devCash !== null ? { devCash: params.devCash } : {}),
    });
    this.matchRoom = null;
    this.enterMatch(link, () => this.startOffline(randomSeed(), 1, 0), skipTicks > 0 ? 'SIMULATING' : 'ESTABLISHING LINK');
  }

  // ------------------------------------------------------------------ match

  private enterMatch(link: MatchLink, rematch: (() => void) | null, loadingText = 'ESTABLISHING LINK', countdownMs = 0): void {
    this.lobby?.dispose();
    this.lobby = null;
    this.screen?.remove();
    this.screen = null;
    this.stopSlowRetry();
    this.session = new GameSession(this.root, {
      world: this.assets!.world,
      link,
      sfx: this.sfx,
      music: this.music,
      loadingText,
      countdownMs,
      onExit: (r) => this.leaveMatch(r, rematch),
      onError: (msg) => {
        this.leaveMatch(null, null);
        setText(this.err, msg.toUpperCase());
      },
      onResumed: () => this.clearRecheck(),
    });
    // chat is online only (an offline match has nobody to talk to)
    this.chat.unmount();
    // (dev `chat=1`: the panel offline too, for layout checks — nothing is sent)
    if (!link.local || (import.meta.env.DEV && params.devChat)) {
      const s = this.session;
      this.chat.mountMatch(() => (this.session === s ? s.chatHost() : null));
    }
    this.renderLink();
  }

  /** Off the match screen, to the menu (online: out of the room too — once the link is back, if it is down). */
  private toMenu(leaveRoom = !!this.room): void {
    const s = this.session;
    if (s && this.sock && !s.isOffline && leaveRoom && !this.sock.send({ type: 'room.leave' })) this.leaveOnReconnect = true;
    s?.dispose();
    this.session = null;
    this.room = null;
    this.matchRoom = null;
    this.clearRecheck();
    if (this.me) this.showMenu();
    else void this.boot();
  }

  private leaveMatch(r: SessionResult | null, rematch: (() => void) | null): void {
    const s = this.session;
    if (!s) return;
    if (!r) {
      this.toMenu(true);
      return;
    }
    // results over the frozen world
    track('results_view');
    this.offlineRematch = rematch;
    this.show(resultsScreen(r.end, r.me, r.players, this.resultActions(), r.matchTicks));
    // a private room's players talk on after the match (the room's chat, ALL), under the standings
    const main = this.screen?.querySelector('.res-main');
    if (main && !s.isOffline && this.matchRoom && !this.matchRoom.quickplay) this.chat.mountLobby(main as HTMLElement, () => this.me?.name ?? '', true);
  }

  /** What the results offer: MENU, plus playing again where it makes sense. */
  private resultActions(): ResultAction[] {
    const menu: ResultAction = { label: 'MENU', run: () => this.toMenu() };
    const rematch = this.offlineRematch;
    if (rematch) {
      return [
        menu,
        {
          label: 'REMATCH',
          primary: true,
          run: () => {
            this.session?.dispose();
            this.session = null;
            rematch();
          },
        },
      ];
    }
    const from = this.matchRoom;
    const room = this.room;
    // (after a reload mid-match we did not see the room before the match: any reopened lobby of ours is it)
    if (room && (!from || room.code === from.code) && !room.quickplay && room.phase === 'lobby') {
      return [
        menu,
        {
          label: 'BACK TO ROOM',
          primary: true,
          run: () => {
            this.session?.dispose();
            this.session = null;
            this.showRoom(room);
          },
        },
      ];
    }
    if (from?.quickplay) {
      return [
        menu,
        {
          label: 'QUICK PLAY',
          primary: true,
          run: () => {
            this.session?.dispose();
            this.session = null;
            this.room = null;
            this.quickPlay();
          },
        },
      ];
    }
    return [menu];
  }

  /** The room reopened (or closed) while the results are up: update their buttons. */
  private refreshResults(): void {
    if (this.screen?.classList.contains('results')) setResultActions(this.screen, this.resultActions());
  }

  // ------------------------------------------------------------------ dev

  private devScreen(name: string): void {
    this.me = { id: 'dev', name: 'NIGHTHAWK-19', email: null, isGuest: true, room: null };
    if (name === 'auth') this.showAuth();
    else if (name === 'authdown') this.showAuth(true);
    else if (name === 'menu') this.showMenu();
    else if (name === 'lobby' || name === 'quickplay') {
      const l = new LobbyScreen(this.assets!.world, this.err);
      this.show(l.el);
      this.lobby = l;
      const qp = name === 'quickplay';
      const slots: PlayerSlot[] = [
        { slot: 0, kind: 'human', userId: 'dev', name: 'NIGHTHAWK-19', team: 1, regionId: 4, ready: true },
        { slot: 1, kind: 'human', userId: 'u2', name: 'Marlowe', team: 2, regionId: 8, ready: true },
        { slot: 2, kind: 'human', userId: 'u3', name: 'Ostrava', team: 3, regionId: null, ready: false },
        qp ? { slot: 3, kind: 'open', name: '', team: 4, regionId: null, ready: false } : { slot: 3, kind: 'bot', name: botCallsign(3), difficulty: 'hard', team: 4, regionId: null, ready: true },
        qp ? { slot: 4, kind: 'open', name: '', team: 5, regionId: null, ready: false } : { slot: 4, kind: 'bot', name: botCallsign(4), difficulty: 'normal', team: 5, regionId: null, ready: true },
        { slot: 5, kind: 'open', name: '', team: 6, regionId: null, ready: false },
        { slot: 6, kind: 'open', name: '', team: 7, regionId: null, ready: false },
        { slot: 7, kind: qp ? 'open' : 'closed', name: '', team: 8, regionId: null, ready: false },
      ];
      const noop = () => {};
      l.update({
        code: 'K7RQX',
        slots: params.teams ? slots.map((x) => ({ ...x, team: (x.slot % 2) + 1 })) : slots,
        settings: params.teams ? { mode: 'teams', maxPlayers: 8, teamCount: 2 } : { mode: 'ffa', maxPlayers: 8 },
        me: 0,
        isHost: !qp,
        hostSlot: qp ? -1 : 0,
        regionSet: params.teams ? [] : (regionSetFor(this.assets!.world, qp ? 8 : 5) ?? []),
        teamRegions: params.teams ? teamLayoutFor(this.assets!.world, 8, 2) : null,
        quickplay: qp ? { fillAt: Date.now() + 14_000, starting: false } : null,
        setSlot: noop,
        pickRegion: noop,
        ready: noop,
        start: noop,
        leave: noop,
      });
    } else if (name === 'results') {
      // `bots=` sets the nations (1 + bots); nation 2 is a second human (the per-human numbers table)
      const n = params.bots + 1;
      const players = ['NIGHTHAWK-19', ...Array.from({ length: n - 1 }, (_, i) => botCallsign(i + 1))].map((nm, i) => ({
        id: i + 1,
        name: nm,
        team: i + 1,
        regionId: i + 1,
        isBot: i > 1,
      }));
      const scores = players.map((_, i) => (i === n - 1 && n > 2 ? 0 : Math.round(412 / (1 + i * 0.6))));
      const human = (id: number, icbm: number, lost: number) => ({
        id,
        region: id,
        score: scores[id - 1],
        areaCurve: [],
        popCurve: [],
        eliminatedTick: null,
        creditsEarned: 41000,
        spendByKind: {},
        icbmLaunched: icbm,
        slbmLaunched: 2,
        bombsDropped: 0,
        intercepted: 6,
        hits: 9,
        citiesLost: lost,
        contestedGains: 80,
        contactTicks: 9000,
      });
      const sample = sampleChronicle();
      const end: EndMessage = {
        type: 'end',
        reason: 'time',
        winners: [1],
        standings: players.map((p, i) => ({
          playerId: p.id,
          score: scores[i],
          areaShare: scores[i] / 1000,
          popShare: scores[i] / 1000,
          killShare: scores[i] / 1000,
          ...(i === n - 1 && n > 2 ? { eliminatedTick: 9000 } : {}),
        })),
        stats: {
          seed: 1,
          mapVersion: '',
          durationTicks: 12000,
          endReason: 'time',
          winnerIds: [1],
          firstContactTick: 1500,
          neutralCurve: [],
          players: [human(1, 14, 1), human(2, 9, 3)],
        },
        chronicle: { ...sample, entries: sample.entries.filter((e) => e.a <= n && e.b <= n) },
      };
      this.show(
        resultsScreen(end, 1, players, [
          { label: 'MENU', run: () => this.showMenu() },
          { label: 'QUICK PLAY', primary: true, run: () => {} },
        ]),
      );
      // the room's chat under the standings, as after an online match (here nothing is sent)
      const main = this.screen?.querySelector('.res-main');
      if (main) this.chat.mountLobby(main as HTMLElement, () => 'NIGHTHAWK-19', true);
    }
  }
}
